import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useLocation } from "react-router-dom";

type SetValue<T> = Dispatch<SetStateAction<T>>;

const WINGSPAN_STATE_PREFIX = "ws_state_";

export function useLocationStateStorage<T>(
  key: string,
  initialValue: T
): [T, SetValue<T>, () => void] {
  const history = useHistory();
  const location = useLocation();
  const prefixedKey = key ? WINGSPAN_STATE_PREFIX + key : "";
  const valueRef = useRef<T>(initialValue);
  const [, forceUpdate] = useState({});

  // Get current value from location state or use initial value
  const storedValue = useMemo(() => {
    if (!prefixedKey) {
      return initialValue;
    }

    const locationState = location.state;
    if (locationState && prefixedKey in locationState) {
      const value = locationState[prefixedKey] as T;
      valueRef.current = value;
      return value;
    }
    return valueRef.current;
  }, [location.state, prefixedKey]);

  // Custom value setter which will update location state
  const setValue: SetValue<T> = useCallback(
    (value: SetStateAction<T>) => {
      // Get current value from location state or ref
      const currentValue =
        history.location.state?.[prefixedKey] !== undefined
          ? (history.location.state[prefixedKey] as T)
          : valueRef.current;

      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(currentValue) : value;

      // Update ref
      valueRef.current = newValue;

      // Update location state by replacing current history entry
      const newState = {
        ...history.location.state,
        [prefixedKey]: newValue
      };

      history.replace({
        hash: history.location.hash,
        pathname: history.location.pathname,
        search: history.location.search,
        state: newState
      });

      // Force re-render to pick up new location state
      forceUpdate({});
    },
    [prefixedKey, history]
  );

  // Clear value from location state
  const clearValue = useCallback(() => {
    const newState = { ...history.location.state };
    delete newState[prefixedKey];

    // Reset to initial value
    valueRef.current = initialValue;

    history.replace({
      hash: history.location.hash,
      pathname: history.location.pathname,
      search: history.location.search,
      state: Object.keys(newState).length > 0 ? newState : undefined
    });

    // Force re-render to pick up new location state
    forceUpdate({});
  }, [prefixedKey, history, initialValue]);

  return [storedValue, setValue, clearValue];
}

// Helper hook for managing multiple values in location state
export function useLocationState<T extends Record<string, any>>(
  initialState?: T
): [T, (updates: Partial<T>) => void, () => void] {
  const history = useHistory();
  const location = useLocation();
  const [, forceUpdate] = useState({});

  const state = useMemo(() => {
    return (location.state as T) || initialState || ({} as T);
  }, [location.state, initialState]);

  const setState = useCallback(
    (updates: Partial<T>) => {
      const newState = {
        ...state,
        ...updates
      };

      history.replace({
        hash: history.location.hash,
        pathname: history.location.pathname,
        search: history.location.search,
        state: newState
      });

      // Force re-render to pick up new location state
      forceUpdate({});
    },
    [state, history]
  );

  const clearState = useCallback(() => {
    history.replace({
      hash: history.location.hash,
      pathname: history.location.pathname,
      search: history.location.search,
      state: undefined
    });

    // Force re-render to pick up new location state
    forceUpdate({});
  }, [history]);

  return [state, setState, clearState];
}

// Example usage:
// const [formData, setFormData, clearFormData] = useLocationStateStorage("formData", { name: "", email: "" });
// const [step, setStep] = useLocationStateStorage("wizardStep", 0);
// const [state, setState, clearState] = useLocationState<{ modal: boolean, data: any }>();
